/**
 * Collection Product List Section JavaScript
 * Handles Swiper initialization for based product sliders
 * CLS-optimized with progressive enhancement
 */

(function() {
  'use strict';

  class CollectionProductList extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.layout = this.dataset.layout;
      this.swiperInstance = null;
      this.isInitialized = false;
      this.infiniteScrollEnabled = this.dataset.infiniteScroll === 'true';
      this.isLoading = false;
      this.observer = null;
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }

    init() {
      this.initializeLayout();
      if (this.infiniteScrollEnabled && this.layout === 'grid') {
        this.initInfiniteScroll();
      }
    }

    initializeLayout() {
      if (this.layout === 'slider' && !this.isInitialized) {
        this.waitForSwiper();
      }
    }

    waitForSwiper() {
      window.loadThemeSwiper().then(() => {
        this.initSlider();
      }).catch((error) => {
        console.warn('Swiper failed to load for CollectionProductList:', error);
      });
    }

    initSlider() {
      const swiperElement = this.querySelector('.swiper');
      if (!swiperElement || this.isInitialized) return;

      const slides = swiperElement.querySelectorAll('.swiper-slide');
      const slideCount = slides.length;

      if (slideCount === 0) return;

      let config = {};
      try {
        config = JSON.parse(swiperElement.dataset.swiperConfig || '{}');
      } catch (e) {
        console.warn('Invalid Swiper config:', e);
      }

      const getSlidesPerView = () => {
        const width = window.innerWidth;
        if (width >= 990 && config.breakpoints && config.breakpoints['990']) {
          return config.breakpoints['990'].slidesPerView || 4;
        } else if (width >= 750 && config.breakpoints && config.breakpoints['750']) {
          return config.breakpoints['750'].slidesPerView || 3;
        }
        return config.slidesPerView || 2;
      };

      const slidesPerView = getSlidesPerView();
      const shouldLoop = config.loop !== false && slideCount > slidesPerView;
      const prevButton = this.querySelector('.product-list__nav--prev');
      const nextButton = this.querySelector('.product-list__nav--next');
      const pagination = this.querySelector('.product-list__pagination');

      const swiperConfig = {
        slidesPerView: config.slidesPerView || 2,
        spaceBetween: config.spaceBetween || 12,
        speed: config.speed || 300,
        loop: shouldLoop,
        loopFillGroupWithBlank: false,
        watchOverflow: true,
        grabCursor: true,
        keyboard: { enabled: true },
        breakpoints: config.breakpoints || {
          '750': { slidesPerView: 3, spaceBetween: 16 },
          '990': { slidesPerView: 4, spaceBetween: 16 }
        },
        on: {
          init: () => {
            this.isInitialized = true;
            swiperElement.classList.add('swiper-initialized');
          }
        }
      };

      if (prevButton && nextButton) {
        swiperConfig.navigation = {
          prevEl: prevButton,
          nextEl: nextButton,
          disabledClass: 'swiper-button-disabled'
        };
      }

      if (pagination) {
        swiperConfig.pagination = { el: pagination, clickable: true };
      }

      try {
        this.swiperInstance = new Swiper(swiperElement, swiperConfig);
        requestAnimationFrame(() => {
          if (this.swiperInstance) {
            this.swiperInstance.update();
            this.swiperInstance.updateSize();
            this.swiperInstance.updateSlides();
            this.swiperInstance.updateProgress();
            this.swiperInstance.updateSlidesClasses();
            this.swiperInstance.slideTo(0, 0, false);
            this.swiperInstance.slides.forEach(slide => {
              slide.style.visibility = 'visible';
              slide.style.opacity = '1';
            });
          }
        });
      } catch (error) {
        console.error('Error initializing Swiper for CollectionProductList:', error);
      }
    }

    initInfiniteScroll() {
      this.grid = this.querySelector('.product-list__grid');
      this.loadingElement = this.querySelector('.product-list__infinite-scroll-loading');
      this.collectionHandle = this.dataset.collectionHandle;

      if (!this.grid || !this.collectionHandle) return;

      this.totalProducts = parseInt(this.grid.dataset.totalProducts, 10) || 0;
      this.currentPage = 1;
      this.loadedProducts = this.grid.querySelectorAll('.product-list__grid-item').length;

      if (this.loadedProducts >= this.totalProducts) {
        this.loadingElement?.classList.add('is-complete');
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isLoading) {
              this.loadMoreProducts();
            }
          });
        },
        { root: null, rootMargin: '200px', threshold: 0 }
      );

      if (this.loadingElement) {
        this.observer.observe(this.loadingElement);
      }
    }

    async loadMoreProducts() {
      if (this.isLoading || this.loadedProducts >= this.totalProducts) return;

      this.isLoading = true;
      this.loadingElement?.classList.add('is-active');

      const nextPage = this.currentPage + 1;

      try {
        const response = await fetch(
          `/collections/${this.collectionHandle}?view=infinite-scroll&page=${nextPage}`
        );

        if (!response.ok) throw new Error('Failed to load products');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newItems = doc.querySelectorAll('.product-list__grid-item');

        if (newItems.length > 0) {
          newItems.forEach((item) => {
            this.grid.appendChild(item.cloneNode(true));
            this.loadedProducts++;
          });
          this.currentPage = nextPage;
          this.grid.dataset.currentPage = nextPage;
        }

        if (this.loadedProducts >= this.totalProducts) {
          this.loadingElement?.classList.add('is-complete');
          this.observer?.disconnect();
        }
      } catch (error) {
        console.error('Error loading more products:', error);
      } finally {
        this.isLoading = false;
        if (this.loadedProducts < this.totalProducts) {
          this.loadingElement?.classList.remove('is-active');
        }
      }
    }

    disconnectedCallback() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  // Register custom element
  if (!customElements.get('product-list')) {
    customElements.define('product-list', CollectionProductList);
  }

  // Handle section reload in Theme Editor
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      const section = event.target.querySelector('product-list');
      if (section && !section.isInitialized) {
        section.initializeLayout?.();
      }
    });

    document.addEventListener('shopify:section:unload', function(event) {
      const section = event.target.querySelector('product-list');
      if (section && section.swiperInstance) {
        section.swiperInstance.destroy(true, true);
        section.swiperInstance = null;
        section.isInitialized = false;
      }
    });
  }

})();
